import { Component, OnInit, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ModalController, NavParams } from '@ionic/angular';

import { NgxQrcodeElementTypes, NgxQrcodeErrorCorrectionLevels} from "@techiediaries/ngx-qrcode";

import { Place } from '@app/modules/model/place';
import { PlaceService } from '@app/modules/model/svcplace';
import { PayAccount } from '@app/modules/model/payaccount';
import { AccountInvoice } from '@app/modules/model/accountinvoice';
import { PrintDevice } from '@app/modules/model/printer';

import { languageSupport, languageService } from '@app/modules/common/language';
import { alertService } from '@app/modules/common/alert';
import { toastService } from '@app/modules/common/toast';
import { printService } from '@app/modules/print';
import { deviceService } from '@app/modules/sockets';
import { dataService } from '@app/modules/data';
import { syncService } from '@app/modules/sync';
import { viewService } from '@app/modules/view';

/************************************/
/* PRINTER CHOOSER                  */
/************************************/

class UiPrinterChooser {
    private _check_interval = null;
    private _check_ticksecs = 0;    // check every 5 seconds

    get Printers(){
        let _printers = [];

        for (let _service of this._place.services){
            if (_service.IsValid){
                for(let _printer of _service.printers){
                    if (_printer.IsValid){
                        _printers.push(_printer);
                    }
                }
            }
        }    

        return _printers;
    }

    private _onlinemap = new Map <PrintDevice, string> ();
    IsOnline(_printer){
        if (this._onlinemap.has(_printer)){
            return this._onlinemap.get(_printer);
        }

        return 'WAITING';
    }

    StartCheck(){
        this._CheckStatus();

        if (this._check_interval == null){
            this._check_interval = this.sync.Clock.OnOneSecondTick.subscribe(
            data => {
                this._check_ticksecs = (this._check_ticksecs + 1) % 5;
                if (this._check_ticksecs == 0){
                    this._CheckStatus();
                }
            });
        }
    }

    StopCheck(){
        if (this._check_interval != null){
            clearInterval(this._check_interval);
            this._check_interval = null;
        }
    }

    /*************************/
    /* STARTS HERE           */
    /*************************/

    constructor(private _place: Place, private change: ChangeDetectorRef, private devsvc: deviceService, private sync: syncService){
        // nothing to do
    }

    OnDestroy(){
        if (this._check_interval != null){
            clearInterval(this._check_interval);
            this._check_interval = null;
        }
    }

    /* SERVICE STATUS CHECK */

    private _CheckStatus(){
        let _services = new Set <PlaceService> ();

        let _printers = this.Printers;
        for (let _printer of _printers){
            _services.add(_printer.rasppi);
        }

        for (let _service of _services){
            this.devsvc.Test(null, null, _service.name, false).then(
            _connection => {
                let _isonline = !!_connection;
                let _toupdate = (_service.IsOnline != _isonline);
                
                _service.IsOnline = _isonline;
                for(let _printer of _service.printers){
                    if (_printers.includes(_printer)){
                        if (!this._onlinemap.has(_printer)){
                            _toupdate = true;
                        }

                        this._onlinemap.set(_printer, _isonline ? 'ONLINE' : 'OFFLINE' );
                    }
                }

                if (_toupdate){
                    this.change.markForCheck();
                }
            });
        }
    }
}    

/************************************/
/* RECAPITULATIVE INVOICE PRINTING  */
/************************************/

@Component({
    selector: 'ui-summary-print',
    templateUrl:'./ui-print/ui-print.html',
    styleUrls: [ 
        './ui-print/ui-print.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiSummaryPrint extends languageSupport implements OnInit, OnDestroy {
    @Input('payment') _payment: AccountInvoice = null;
    @Input('proforma') _proforma = false;

    private get _account() : PayAccount{
        return this._payment?.account || null;
    }

    private get _place() : Place {
        return this._account?.place || null;
    }

    private LoadTickets(){
        return new Promise((resolve) => {
            if (this._proforma){
                resolve(this._payment['tickets'])
            }
            else {
                // Caso post-pago: la invoice ya tiene los tickets pegados
                // in-memory via _OnPayAccount.AddTicket. Sin recurso al
                // server. LoadPlaceTickets aqui devolvia un subset por
                // ventana de fechas / desfase de paidon vs invoice.updated.
                const _localTickets = this._payment.tickets;
                if (_localTickets && _localTickets.length > 0){
                    this.data.LoadTicketObjects(_localTickets).then(
                    (tickets) => {
                        resolve(tickets);
                    });
                    return;
                }
                // Fallback post-reload: la invoice se reconstruyo desde
                // DB sin tickets pegados; recuperar del server por rango.
                this.data.LoadPlaceTickets(this._place, this._payment.since, this._payment.updated, true).then(
                    data => {
                        const _paymentId = this._payment?.objid;
                        let _tickets = [];
                        for (let _ticket of data){
                            const _ticketInvId = _ticket.invceac ? _ticket.invceac.objid : null;
                            if ((_ticket.IsValid) && (_ticket.IsPaid) && _paymentId && _ticketInvId == _paymentId && (_ticket.price != 0)){
                                _tickets.push(_ticket);
                            }
                        }

                        this.data.LoadTicketObjects(_tickets).then(
                        (tickets) => {
                            resolve(tickets);
                        });
                    });
            }
        });
    }

    private _ticketlists = null;
    private get TicketList(){
        return this._ticketlists;
    }

    private set TicketList(value){
        this._ticketlists = value;
        this.change.markForCheck();
    }

    private get MerchantInfo(){
        let _account = this._account;
        return {
            name: _account.business,
            taxid: _account.taxid,
            address: _account.address
        };
    }

    private get BusinessInfo(){
        let _place = this._place;
        return {
            name: _place.business || _place.name,
            taxid: _place.taxid,
            address: _place.fiscal ? _place.fiscal.formatted : _place.address.formatted
        };
    }

    private get PaymentAmount(){
        let _taxrates = {};
        for(let _ticket of this.TicketList){
            let _splitted = _ticket.SplitTaxes;
            for (let _taxrate of _splitted){
                if (!(_taxrate.rate in _taxrates)){
                    _taxrates[_taxrate.rate] = 0;
                }
                _taxrates[_taxrate.rate] += _taxrate.total;
            }
        }

        let _splited = [];

        for (let _taxrate in _taxrates){
            let _total = _taxrates[_taxrate];
            let _base = _total / ((100 + Number(_taxrate))/100);
            let _taxes = _total- _base;
    
            _splited.push({
                rate: Number(_taxrate),
                total: Math.round(_total * 100)/100,
                base: Math.round(_base * 100)/100,
                taxes: Math.round(_taxes * 100)/100
            })
        }

        let _total = 0;
        let _base = 0;
        let _taxes = 0;

        for (let _rate of _splited){
            _total += _rate.total;
            _taxes += _rate.taxes;
            _base += _rate.base;
        }

        return {
            total: Math.round(_total * 100) / 100,
            base: Math.round(_base * 100) / 100,
            taxes: Math.round(_taxes * 100) / 100,
            rates: _splited
        };
    }

    private get PaymentInvoice(){
        if (this._payment.invoice){
            return this._payment.series + this._payment.invoice;
        }

        return null;
    }

    private _brief = null;
    get PaymentInfo(){
        if ((this._brief == null) && (this.TicketList)){
            let _brief = {
                logo: this._place.PrintLogo ? this._place.logo : null,
                created: this._payment.created,
                isinvoice: false,
                invoice: null,
                merchant: null,
                business: this.BusinessInfo,    
                since: this._payment.since,
                until: this._payment.until,    
                tickets: this.TicketList,
                amount: this.PaymentAmount,

                /* ticketbai info and code */
                ticketbai: false,
                ticketbai_info: null,
                ticketbai_code: null,

                /* verifactu QR code */
                verifactu: false,
                verifactu_code: null                
            }

            if ((this._payment.invoice != null) || (this._proforma)){
                _brief.isinvoice = !!this._payment.invoice;
                _brief.invoice = this.PaymentInvoice;
                _brief.merchant = this.MerchantInfo;
            }

            _brief.ticketbai = (this._payment.ticketbai != null);
            if (_brief.ticketbai){
                _brief.ticketbai_info = this._payment.ticketbai.id;            
                _brief.ticketbai_code = {
                    _qr_element_type: NgxQrcodeElementTypes.URL,
                    _qr_correction_level: NgxQrcodeErrorCorrectionLevels.HIGH,
                    _qr_element_value: this._payment.ticketbai.qr
                };            
            }

            _brief.verifactu = (this._payment.verifactu != null);
            if (_brief.verifactu) {
                _brief.verifactu_code = {
                    _qr_element_type: NgxQrcodeElementTypes.URL,
                    _qr_correction_level: NgxQrcodeErrorCorrectionLevels.HIGH,
                    _qr_element_value: this._payment.verifactu.qr
                };            
            }

            this._brief = _brief;    
        }

        return this._brief;
    }
   
    /*************************/
    /* STARTS HERE           */
    /*************************/

    constructor(private lang: languageService, private change: ChangeDetectorRef, private alertCtrl: alertService, private toast: toastService, private printer: printService, private data: dataService, private sync: syncService, public view: viewService) {
        super(lang, change);
    }
    
    ngOnInit(){
        this.LoadTickets().then(
        data => {
            this.TicketList = data;
            this.SetLanguage(this._place.Language).then(
            data => {
                this.change.markForCheck();    
            });
        });
    }
    
    ngOnDestroy(){
        super.OnDestroy();
    }

    /**************************/
    /* EVENTS HANDLER         */
    /**************************/

    @ViewChild('printable', {static: false} ) _printable: ElementRef;
    async DoPrint(place, device){
        if (this._printable){   // send the ticket to the device
            await this.printer.PrintPayment(place, device, this._printable.nativeElement);
        }        
    }

    private async _OnPaymentSent(email){
        let alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_ticket_sent_invoice'),
            message: this.lang.tr('@place_ticket_sent_message', [ email ]),
            backdropDismiss: true,
            buttons: [{
                text: this.lang.tr('@ok'),
                handler: (data) => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        await alert.present();
    }

    async DoSend(place){
        let _doSendUrl = 'printer/service/dosend.php';
        if (this._printable){
            this.toast.ShowWait();

            let blob = await this.printer.TicketToPdf(place, this._printable.nativeElement);
            if (blob){
                let _pdfname = 'proforma';
                if (this._payment.series && this._payment.invoice){
                    _pdfname = this._payment.series + this._payment.invoice;
                }

                let _params = {
                    lang: place.Language,
                    invoice: _pdfname + '.pdf',
                    recipient: this._account.client.email
                };

                this.sync.DoRequest(_doSendUrl, _params, blob).then(
                data => {
                    this.toast.HideWait();

                    if (data['errorcode'] == 0){
                        this._OnPaymentSent(this._account.client.email);
                    }
                    else {
                        console.error("[SERVICE] Error [" +  data['errorcode'] + "] in '" + _doSendUrl + "'");
                        this.toast.ShowAlert('danger', this.lang.tr('@service_request_error_' + data['errorcode']));
                    }                     
                }, error => {
                    console.error("[REQUEST] Error [" + error.status + "] in http request '" + _doSendUrl + "'");
                    this.toast.HideWait();
                });
            }
        }
    }
}

/************************************/
/* RECAPITULATIVE INVOICE MODAL     */
/************************************/

@Component({
    selector: 'modal-summary-invoice',
    templateUrl:'./ui-modal/ui-modal.html',
    styleUrls: [ 
        './ui-modal/ui-modal.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiSummaryModal extends languageSupport implements OnInit, OnDestroy {
    public _payment = null;
    public _proforma = false;   // only provided when is proforma invoice

    private get _account() : PayAccount{
        return this._payment?.account || null;
    }

    private get _place() : Place {
        return this._account?.place || null;
    }

    get HasOptions(){
        return this.CanSendInvoice;
    }

    private _showoptions = false;
    get ShowOptions(){
        return this._showoptions;
    }

    set ShowOptions(value){
        this._showoptions = value;
        this.change.markForCheck();
    }

    get CanSendInvoice(){      // true if we have the business email
        return (this._account) && (this._account.client) && (this._account.client.email != null)
    }

    /*************************/
    /* STARTS HERE           */
    /*************************/

    private _printerchooser = null;
    get PrinterChooser(){
        if (this._printerchooser == null){
            this._printerchooser = new UiPrinterChooser(this._place, this.change, this.devsvc, this.sync);
        }
        return this._printerchooser;
    }

    constructor(lang: languageService, private change: ChangeDetectorRef, private modalCtrl: ModalController, private params: NavParams, private devsvc: deviceService, private sync: syncService, public view: viewService) {
        super(lang, change);
    }
    
    public _printing = false;
    ngOnInit(){
        this._payment = this.params.get('payment');
        this._proforma = this.params.get('proforma') || false;
    }
    
    ngOnDestroy(){
        super.OnDestroy();

        if (this._printerchooser){
            this._printerchooser.OnDestroy();
            this._printerchooser = null;
        }
    }

    OnClose(){
        this.modalCtrl.dismiss(null);
    }

    OnOptions(){
        this.ShowOptions = !this.ShowOptions;
    }    

    /*************************/
    /* SEND EMAIL            */
    /*************************/

    SendPayment(){
        this.ShowOptions = false;

        if (this._printview){
            this._printview.DoSend(this._place);
        }
    }

    /*************************/
    /* PRINT TO DEVICE       */
    /*************************/

    @ViewChild('printview', {static: false} ) _printview: UiSummaryPrint;

    get MultiplePOS(){
        return this._place.MultiplePOS;
    }

    PrinterTill(_printer){
        return _printer.rasppi?.name || null;
    }

    private _showprinters = false;
    get ShowPrinters(){
        return this._showprinters;
    }

    get Printers(){
        return this.PrinterChooser.Printers;
    }

    IsOnline(_printer){
        return this.PrinterChooser.IsOnline(_printer);
    }

    OnHidePrinters(){
        this._showprinters = false;

        this.PrinterChooser.StopCheck();    
        this.change.markForCheck();
    }
    
    ToPrinter(_printer, closemodal = true){
        if (this._printview){
            this._printing = true;
            this._printview.DoPrint(this._place, _printer).then(
            data => {
                this._printing = false;
                if (closemodal){
                    this.OnClose();
                }
            });
        }        
    }

    PrintPayment(){
        if (this._place.till){  // is this is a POS device, send to printer
            let _hasdevice = false;

            for(let _printer of this._place.till.printers){
                if (!_hasdevice && (_printer.type == 'BAR')) {
                    this.ToPrinter(_printer, false);
                    _hasdevice = true;          // sent to a printer device
                }
            }

            if (!_hasdevice){
                this.ToPrinter(null, false);    // no printers: use browser printing
            }

            this.OnClose();
        }
        else {  // otherwise must choose a printer
            if (!this._showprinters){
                this._showprinters = true;

                this.PrinterChooser.StartCheck();    
                this.change.markForCheck();
            }
        }
    }
}